import React, { useEffect, useMemo, useState } from "react";
import Modal from "../Modal";
import api from "../../utils/api";
import Menu from "../Menu";

type RoleOption = "Operator" | "SuperAdmin";

export interface EditUser {
  userId: string;
  name: string;
  email: string;
  phoneNo?: string | null;
  role?: string | null;
  displayUserId?: string | null;
}

interface EditUserModalProps {
  isOpen: boolean;
  user: EditUser | null;
  onClose: () => void;
  onSaved?: () => void;
}

const ROLE_OPTIONS: RoleOption[] = ["Operator", "SuperAdmin"];

const normalizePhone = (value: string) => value.replace(/[^0-9A-Za-z]/g, "");

const formatPhone = (value: string) => {
  const cleaned = normalizePhone(value).slice(0, 10);
  const part1 = cleaned.slice(0, 3);
  const part2 = cleaned.slice(3, 6);
  const part3 = cleaned.slice(6, 10);
  return [part1, part2, part3].filter(Boolean).join("-");
};

const EditUserModal: React.FC<EditUserModalProps> = ({ isOpen, user, onClose, onSaved }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState<RoleOption>("Operator");
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      setName(user.name || "");
      setEmail((user.email || "").toLowerCase());
      setPhone(formatPhone(user.phoneNo || ""));
      const normalizedRole = (user.role as RoleOption) || "Operator";
      setRole(ROLE_OPTIONS.includes(normalizedRole) ? normalizedRole : "Operator");
      setFieldErrors({});
      setError(null);
    }
  }, [isOpen, user]);

  const roleLabel = useMemo(() => (role === "SuperAdmin" ? "Super Admin" : role), [role]);

  if (!isOpen || !user) {
    return null;
  }

  const validate = () => {
    const errors: Record<string, string | null> = {};
    if (!name.trim()) {
      errors.name = "Name is required.";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      errors.email = "Email is required.";
    } else if (!emailRegex.test(email.trim().toLowerCase())) {
      errors.email = "Enter a valid email.";
    }
    const normalizedPhone = normalizePhone(phone);
    if (normalizedPhone && normalizedPhone.length !== 10) {
      errors.phone = "Enter a 10 digit phone number.";
    }
    setFieldErrors(errors);
    return Object.values(errors).some(Boolean);
  };

  const handleSave = async () => {
    if (!user) {
      return;
    }
    if (validate()) {
      setError("Please fix the highlighted fields.");
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const normalizedEmail = email.trim().toLowerCase();
      const formattedPhone = normalizePhone(phone);
      const currentUserId = localStorage.getItem("userId") || undefined;
      await api.put(`/users/update-user-details/${user.userId}`, {
        name: name.trim(),
        email: normalizedEmail,
        phoneNo: formattedPhone || null,
        role,
        modifiedBy: currentUserId,
        deleted: false,
      });
      onSaved?.();
      onClose();
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to update user.";
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onCancel={onClose}
      title="Edit User"
      hideButtons
      description="Update core account information for this user."
    >
      <div className="modal-form">
        <div className="modal-field-group">
          <label>
            Full Name
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Firstname Lastname"
              maxLength={60}
            />
          </label>
          {fieldErrors.name && <div className="modal-error">{fieldErrors.name}</div>}
        </div>

        <div className="modal-field-group">
          <label>
            Email
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@gmail.com"
            />
          </label>
          {fieldErrors.email && <div className="modal-error">{fieldErrors.email}</div>}
        </div>

        <div className="modal-field-group">
          <label>
            Phone Number
            <input
              value={phone}
              maxLength={12}
              onChange={(e) => setPhone(formatPhone(e.target.value))}
              placeholder="123-456-7890"
            />
          </label>
          {fieldErrors.phone && <div className="modal-error">{fieldErrors.phone}</div>}
        </div>

        <div className="modal-field-group">
          <label>Role</label>
          <Menu
            items={ROLE_OPTIONS.map((option) => ({
              key: option.toLowerCase(),
              label: option === "SuperAdmin" ? "Super Admin" : option,
              selected: role === option,
              onSelect: () => setRole(option),
            }))}
            closeOnSelect
            trigger={
              <button type="button" className="menu-trigger modal-menu-trigger">
                {roleLabel}
              </button>
            }
            dropdownClassName="modal-menu-dropdown"
          />
        </div>
      </div>

      {error && <div className="modal-error">{error}</div>}

      <div className="modal-actions-inline">
        <button className="button-secondary" onClick={onClose} disabled={isSaving}>
          Cancel
        </button>
        <button className="button-primary" onClick={handleSave} disabled={isSaving}>
          {isSaving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </Modal>
  );
};

export default EditUserModal;
